import { InfoBox } from '@hotjar/webapp/components/InfoBox/InfoBox';

import type { PriceSummary } from '@hotjar/webapp/billing/subscription';
import { usePrettifyPrice } from '@hotjar/webapp/hooks/usePrettifyPrice';
import { supportsMultipleSku } from '@hotjar/webapp/domains/common/product/features';
import type { SubscriptionSummaryAccount } from '../SubscriptionSummary';
import { Header, SwitchLink } from '../SubscriptionSummary.styles';
import { FooterCTA } from './SubscriptionCost.styles';
import { CostBreakdown } from './Totals';

interface Props {
  account: SubscriptionSummaryAccount;
  onSubmit: (x: string) => void;
  hasPaidPlan: boolean;
  subscriptionPriceSummary: PriceSummary | null;
}

// TODO: future human - add translations everywhere!!
export const LegacySubscriptionCost = ({
  account,
  onSubmit,
  hasPaidPlan,
  subscriptionPriceSummary,
}: Props): JSX.Element => {
  const { prettifyPrice } = usePrettifyPrice();
  const isMonthlyBilling = account && account.billingCycle === 'monthly';
  const isYearlyBilling = account && account.billingCycle === 'yearly_recurring';

  const title = <Header>Cost per {`${isMonthlyBilling ? 'month' : 'year'}`}</Header>;
  let footerCTA;

  const subscriptionTotal = subscriptionPriceSummary?.total.toNumber() ?? 0;
  const subscriptionVatRate = subscriptionPriceSummary?.tax.rate ?? 0;
  const subscriptionVatTotal = subscriptionPriceSummary?.tax.amount.toNumber() ?? 0;
  const totalWithTax = subscriptionPriceSummary?.totalWithTax.toNumber() ?? 0;

  const shouldShowVatRate = subscriptionTotal !== 0 && subscriptionVatRate !== 0;

  if (!supportsMultipleSku(account) && !hasPaidPlan) {
    footerCTA = (
      <SwitchLink
        onClick={() => onSubmit('business')}
        variant="primary"
        data-testid="switch-monthly"
      >
        Get more insights with Hotjar Business
      </SwitchLink>
    );
  } else if (hasPaidPlan && isYearlyBilling) {
    footerCTA = (
      <FooterCTA>
        <SwitchLink
          onClick={() => onSubmit('monthly')}
          variant="primary"
          data-testid="switch-monthly"
        >
          Contact support to switch to monthly
        </SwitchLink>
      </FooterCTA>
    );
  } else if (hasPaidPlan && isMonthlyBilling) {
    footerCTA = (
      <FooterCTA>
        <SwitchLink onClick={() => onSubmit('yearly_recurring')} variant="primary">
          Switch to yearly
        </SwitchLink>
      </FooterCTA>
    );
  } else {
    footerCTA = null;
  }

  return (
    <InfoBox
      title={title}
      body={
        <CostBreakdown
          subscriptionTotal={subscriptionTotal}
          subscriptionVatRate={subscriptionVatRate}
          subscriptionVatTotal={subscriptionVatTotal}
          totalWithTax={totalWithTax}
          prettifyCost={prettifyPrice}
          hasPaidPlan={hasPaidPlan}
          shouldShowVatRate={shouldShowVatRate}
        />
      }
      footer={footerCTA}
    />
  );
};
